import femaleStaffImage from '@/assets/images/reason-female-staff.webp';
import sameDayImage from '@/assets/images/reason-same-day.webp';
import noAttendanceImage from '@/assets/images/reason-no-attendance.jpg';
import valuablesImage from '@/assets/images/reason-valuables.webp';
import recycleImage from '@/assets/images/reason-recycle.webp';
import neighborsImage from '@/assets/images/reason-neighbors.webp';

export default function WhyChooseSection() {
  const reasons = [
    {
      icon: 'ri-time-line',
      title: '最短即日対応',
      description: 'お急ぎの場合でもご安心ください。お問い合わせいただいたその日のうちに現地調査・お見積りが可能です。',
      image: sameDayImage,
    },
    {
      icon: 'ri-women-line',
      title: '女性スタッフ在籍',
      description: '一人暮らしの女性のお客様も安心してご利用いただけるよう、女性スタッフによる作業も承っております。',
      image: femaleStaffImage,
    },
    {
      icon: 'ri-key-line',
      title: '立ち会い不要',
      description: '遠方にお住まいの方や、お忙しい方のために、鍵をお預かりしての作業も可能です。作業完了後は写真でご報告いたします。',
      image: noAttendanceImage,
    },
    {
      icon: 'ri-search-eye-line',
      title: '貴重品探索',
      description: '権利書、通帳、現金、思い出の品など、作業中に見つかった貴重品は必ずお客様にお渡しいたします。',
      image: valuablesImage,
    },
    {
      icon: 'ri-recycle-line',
      title: '買取・リサイクル',
      description: '価値のある不用品は高価買取いたします。清掃費用から差し引くことで、リーズナブルな価格を実現します。',
      image: recycleImage,
    },
    {
      icon: 'ri-chat-smile-3-line',
      title: '近隣への配慮',
      description: '作業中の騒音や搬出時の目隠しなど、近隣住民の方々に最大限配慮して作業を行います。',
      image: neighborsImage,
    }
  ];

  return (
    <section id="reasons" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full mb-6">
              <i className="ri-star-line text-primary"></i>
              <span className="text-primary font-semibold text-sm">選ばれる理由</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              信頼と実績の <strong className="text-primary">プロフェッショナル</strong>
            </h2>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              創業以来、数多くのゴミ屋敷清掃や遺品整理に携わってきました。
              お客様一人ひとりのご事情に寄り添い、丁寧かつ迅速なサービスを提供いたします。
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-2xl text-primary"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">秘密厳守の徹底</h4>
                  <p className="text-gray-600">個人情報の保護はもちろん、作業時の服装や車両にも配慮し、周囲に知られないよう作業します。</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-2xl text-primary"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">安心の明朗会計</h4>
                  <p className="text-gray-600">お見積り後の追加料金は一切いただきません。作業内容と費用を明確にご説明してから作業に入ります。</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-2xl text-primary"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">徹底した清掃品質</h4>
                  <p className="text-gray-600">ただ片付けるだけでなく、ハウスクリーニングの技術を活かし、見違えるような綺麗さを実現します。</p>
                </div>
              </div>
            </div>

            <a 
              href="#contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white font-semibold rounded-lg hover:bg-pink-600 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              無料お見積りを依頼する
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* Right Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {reasons.map((reason, index) => (
              <div
                key={index}
                className="rounded-xl relative overflow-hidden min-h-[180px] hover:shadow-xl transition-all group"
              >
                {/* 背景画像 */}
                <img src={reason.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                {/* 黒半透明オーバーレイ */}
                <div className="absolute inset-0 bg-black/55 group-hover:bg-black/45 transition-all"></div>
                {/* テキスト */}
                <div className="relative z-10 p-6 flex flex-col justify-end h-full min-h-[180px]">
                  <h3 className="text-lg font-bold text-white mb-2">{reason.title}</h3>
                  <p className="text-sm text-gray-200 leading-relaxed">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}